import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
} from 'react-admin';
import { Box, Typography, Alert } from '@mui/material';

const entityTypeChoices = [
  { id: 'cricket_player', name: 'Cricket Player' },
  { id: 'movie', name: 'Movie' },
  { id: 'movie_song', name: 'Movie Song' },
  { id: 'city', name: 'City' },
  { id: 'company', name: 'Company' },
];

// Templates for different entity types
const attributeTemplates: Record<string, object> = {
  cricket_player: {
    basic: {
      name: '',
      birthDate: '',
      nationality: '',
    },
    career: {
      domestic: {
        ipl: {
          teams: [],
          totals: {
            runs: 0,
            wickets: 0,
            matches: 0,
          },
        },
      },
    },
  },
  movie: {
    basic: {
      releaseDate: '',
      runtime: 0,
    },
    financial: {
      boxOffice: {
        worldwide: 0,
        domestic: 0,
      },
    },
    people: {
      directors: [],
      cast: [],
    },
    categories: {
      genres: [],
    },
  },
  movie_song: {
    basic: {
      title: '',
      releaseYear: 0,
      duration: 0,
      language: '',
    },
    movie: {
      title: '',
      year: 0,
      genre: [],
    },
    people: {
      singers: [],
      composers: [],
      lyricists: [],
      directors: [],
    },
  },
  city: {},
  company: {},
};

const validateJson = (value: unknown) => {
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
    } catch {
      return 'Invalid JSON';
    }
  }
  return undefined;
};

// JSON input component
const JsonInput = ({ source, label }: { source: string; label: string }) => {
  return (
    <TextInput
      source={source}
      label={label}
      multiline
      fullWidth
      minRows={15}
      maxRows={30}
      validate={validateJson}
      format={(value: unknown) =>
        typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '{}')
      }
      parse={(value: string) => {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }}
    />
  );
};

export const EntityCreate = () => {
  return (
    <Create>
      <SimpleForm
        defaultValues={{
          attributes: attributeTemplates.cricket_player,
        }}
        warnWhenUnsavedChanges
      >
        <Box sx={{ width: '100%', maxWidth: 800 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Create a new entity. The attributes JSON structure depends on the entity type.
            Select an entity type to see the template.
          </Alert>

          <TextInput
            source="display_name"
            fullWidth
            required
            validate={required()}
            helperText="The display name shown to users"
          />

          <SelectInput
            source="entity_type"
            choices={entityTypeChoices}
            fullWidth
            required
            validate={required()}
            helperText="Select the type of entity"
          />

          <TextInput
            source="search_tokens"
            fullWidth
            format={(value: string[]) => (Array.isArray(value) ? value.join(', ') : '')}
            parse={(value: string) =>
              value
                .split(',')
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean)
            }
            helperText="Comma-separated search terms (e.g., 'virat, kohli, rcb')"
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Attributes (JSON)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Enter the entity attributes as JSON. Structure depends on entity type.
          </Typography>

          <JsonInput source="attributes" label="Attributes" />
        </Box>
      </SimpleForm>
    </Create>
  );
};
